import axios from 'axios';
import { SessionManager } from '../utils/sessionManager';

const API_BASE_URL = process.env.REACT_APP_API_URL;

/**
 * API Client with automatic token injection
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }    
});

// Request interceptor to add token and update activity
let requestCounter = 0;
apiClient.interceptors.request.use(
  (config) => {
    requestCounter++;
    
    const token = SessionManager.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      // Update activity on every API request
      SessionManager.updateActivity();
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => {
    // Update activity on successful response
    SessionManager.updateActivity();
    return response;
  },
  (error) => {
    // Don't redirect on network errors or timeouts
    if (!error.response) {
      // console.error('Network error:', error.message);
      return Promise.reject(error);
    }
    
    if (error.response?.status === 401) {
      const errorCode = error.response?.data?.code;
      const errorMessage = error.response?.data?.message || '';
      
      // Only redirect to login on authentication errors, not on verification failures
      if (errorCode === 'INACTIVITY_TIMEOUT' || 
          errorCode === 'TOKEN_EXPIRED' || 
          errorCode === 'INVALID_TOKEN' ||
          errorMessage.includes('Session expired') ||
          errorMessage.includes('Authentication required') ||
          errorMessage.includes('Invalid token') ||
          errorMessage.includes('Token expired')) {
        
        // Clear session
        SessionManager.clearSession();
        
        // Show user-friendly message
        const message = errorCode === 'INACTIVITY_TIMEOUT' 
          ? 'Your session expired due to 15 minutes of inactivity. Please login again.'
          : 'Your session has expired. Please login again.';
        
        // Store message for login page
        sessionStorage.setItem('logout_reason', message);
        
        // Redirect to login only once
        if (!window.location.pathname.includes('/login') && window.location.pathname !== '/') {
          window.location.href = '/';
        }
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Authentication API
 */
export const authAPI = {
  login: async (email, password) => {
    const response = await apiClient.post('/auth/login', { email, password });
    return response.data;
  },

  register: async (userData) => {
    const response = await apiClient.post('/auth/register', userData);
    return response.data;
  },

  sendOTP: async (data) => {
    const response = await apiClient.post('/auth/send-otp', data);
    return response.data;
  },

  verifyOTP: async (data) => {
    const response = await apiClient.post('/auth/verify-otp', data);
    return response.data;
  },

  verifyIPOTP: async (email, otp, ipAddress) => {
    const response = await apiClient.post('/auth/verify-ip-otp', { email, otp, ipAddress });
    return response.data;
  },

  logout: async () => {
    const response = await apiClient.post('/auth/logout');
    SessionManager.clearSession();
    return response.data;
  },

  getProfile: async () => {
    const response = await apiClient.get('/auth/profile');
    return response.data;
  },

  updateProfile: async (data) => {
    const response = await apiClient.put('/auth/profile', data);
    return response.data;
  },

  sendEmailChangeOTP: async (newEmail) => {
    const response = await apiClient.post('/auth/email-change/send-otp', { newEmail });
    return response.data;
  },

  verifyEmailChangeOTP: async (otp) => {
    const response = await apiClient.post('/auth/email-change/verify-otp', { otp });
    return response.data;
  },

  sendPasswordResetOTP: async (data) => {
    const response = await apiClient.post('/auth/send-password-reset-otp', data);
    return response.data;
  },

  verifyPasswordResetOTP: async (data) => {
    const response = await apiClient.post('/auth/verify-password-reset-otp', data);
    return response.data;
  },

  resetPassword: async (data) => {
    const response = await apiClient.post('/auth/reset-password', data);
    return response.data;
  },

  generateAPITokens: async () => {
    const response = await apiClient.post('/auth/generate-tokens');
    return response.data;
  },

  getAPITokens: async () => {
    const response = await apiClient.get('/auth/tokens');
    return response.data;
  }
};

/**
 * Verification API
 */
export const verificationAPI = {
  verifyPAN: async (pan) => {
    const response = await apiClient.post('/verify/pan', { pan });
    return response.data;
  },

  verifyPAN360: async (pan) => {
    const response = await apiClient.post('/verify/pan-360', { pan });
    return response.data;
  },

  verifyEmployment360: async (uan) => {
    const response = await apiClient.post('/verify/employment-360', { uan });
    return response.data;
  },

  verifyPANToTAN: async (pan) => {
    const response = await apiClient.post('/verify/pan-to-tan', { pan });
    return response.data;
  },

  verifyTAN: async (tan) => {
    const response = await apiClient.post('/verify/tan', { tan });
    return response.data;
  },

  verifyMSME: async (urn) => {
    const response = await apiClient.post('/verify/msme', { urn });
    return response.data;
  },

  verifyGSTIN: async (gstin) => {
    const response = await apiClient.post('/verify/gstin', { gstin });
    return response.data;
  },

  verifyGSTINLite: async (gstin) => {
    const response = await apiClient.post('/verify/gstin-lite', { gstin });
    return response.data;
  },

  searchGSTINByPAN: async (pan) => {
    const response = await apiClient.post('/verify/gstin-by-pan', { pan });
    return response.data;
  },

  verifyBankAccount: async (accountNumber, ifsc, name) => {
    const response = await apiClient.post('/verify/bank-account', {
      accountNumber,
      ifsc,
      name
    });
    return response.data;
  },

  verifyCIN: async (cin) => {
    const response = await apiClient.post('/verify/cin', { cin });
    return response.data;
  },

  verifyDrivingLicense: async (dlNumber, dob) => {
    const response = await apiClient.post('/verify/driving-license', {
      dlNumber,
      dob
    });
    return response.data;
  },

  verifyVoterID: async (epicNumber) => {
    const response = await apiClient.post('/verify/voter-id', {
      epicNumber
    });
    return response.data;
  },

  verifyVehicleRC: async (regNo) => {
    const response = await apiClient.post('/verify/vehicle-rc', {
      regNo
    });
    return response.data;
  },

  verifyPassport: async (fileNumber, dob) => {
    const response = await apiClient.post('/verify/passport', {
      fileNumber,
      dob
    });
    return response.data;
  },

  verifyUdyam: async (udyamNumber) => {
    const response = await apiClient.post('/verify/udyam', { udyamNumber });
    return response.data;
  },

  downloadUdyamCertificate: async (udyamNumber) => {
    const response = await apiClient.post('/verify/udyam-certificate', { udyamNumber }, {
      responseType: 'arraybuffer'
    });
    // Trigger download
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Udyam_Certificate_${udyamNumber}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    return { success: true, message: 'Certificate downloaded' };
  },

  verifyPANToUdyam: async (pan) => {
    const response = await apiClient.post('/verify/pan-to-udyam', { pan: pan || undefined });
    return response.data;
  },

  verifyPANToUdyamLite: async (pan) => {
    const response = await apiClient.post('/verify/pan-to-udyam-lite', { pan });
    return response.data;
  },

  verifyCIBIL: async (pan) => {
    const response = await apiClient.post('/verify/cibil', { pan });
    return response.data;
  },

  verifyPANToCIN: async (pan) => {
    const response = await apiClient.post('/verify/pan-to-cin', { pan });
    return response.data;
  },

  getHistory: async (limit = 50, offset = 0) => {
    const response = await apiClient.get('/verify/history', {
      params: { limit, offset }
    });
    return response.data;
  },

  // Bulk verification methods
  uploadBulkVerification: async (file, verificationTypeId) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('verificationTypeId', verificationTypeId);

    const response = await apiClient.post('/verify/bulk/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  getBulkJobs: async (limit = 50, offset = 0) => {
    const response = await apiClient.get('/verify/bulk/jobs', {
      params: { limit, offset }
    });
    return response.data;
  },

  getBulkJobDetails: async (bulkJobId) => {
    const response = await apiClient.get(`/verify/bulk/jobs/${bulkJobId}`);
    return response.data;
  },

  downloadBulkResults: async (bulkJobId) => {
    const response = await apiClient.get(`/verify/bulk/jobs/${bulkJobId}/download`, {
      responseType: 'blob'
    });
    
    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = `bulk_verification_results_${bulkJobId}.csv`; // fallback
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/"/g, '');
      }
    }
    
    return { blob: response.data, filename };
  },

  cancelBulkJob: async (bulkJobId) => {
    const response = await apiClient.post(`/verify/bulk/jobs/${bulkJobId}/cancel`);
    return response.data;
  }
};

/**
 * Credit API
 */
export const creditAPI = {
  getBalance: async () => {
    const response = await apiClient.get('/credits/balance');
    return response.data;
  },

  getStats: async () => {
    const response = await apiClient.get('/credits/stats');
    return response.data;
  },

  addCredits: async (userId, amount) => {
    const response = await apiClient.post('/credits/add', { userId, amount });
    return response.data;
  },

  getTransferLogs: async (limit = 200, offset = 0) => {
    const response = await apiClient.get('/credits/transfer-logs', {
      params: { limit, offset }
    });
    return response.data;
  }
};

/**
 * Pricing API (for clients)
 */
export const pricingAPI = {
  getAll: async () => {
    const response = await apiClient.get('/pricing');
    return response.data;
  }
};

/**
 * Admin API
 */
export const adminAPI = {
  getAllUsers: async (role, limit = 50, offset = 0) => {
    const response = await apiClient.get('/admin/users', {
      params: { role, limit, offset }
    });
    return response.data;
  },

  getPendingUsers: async () => {
    const response = await apiClient.get('/admin/users/pending');
    return response.data;
  },

  approveUser: async (userId) => {
    const response = await apiClient.post('/admin/users/approve', { userId });
    return response.data;
  },

  rejectUser: async (userId) => {
    const response = await apiClient.post('/admin/users/reject', { userId });
    return response.data;
  },

  sendAdminCreationOTP: async (email) => {
    const response = await apiClient.post('/admin/users/send-admin-otp', { email });
    return response.data;
  },

  verifyAdminCreationOTP: async (email, otp) => {
    const response = await apiClient.post('/admin/users/verify-admin-otp', { email, otp });
    return response.data;
  },

  createAdmin: async (firstName, lastName, companyName, email, password, phoneNumber, category, states, membershipNumber) => {
    const response = await apiClient.post('/admin/users/create-admin', {
      firstName,
      lastName,
      companyName,
      email,
      password,
      phoneNumber,
      category,
      states,
      membershipNumber
    });
    return response.data;
  },

  getPlatformStats: async () => {
    const response = await apiClient.get('/admin/stats');
    return response.data;
  },

  updateUserStatus: async (userId, isActive) => {
    const response = await apiClient.put('/admin/users/status', {
      userId,
      isActive
    });
    return response.data;
  },

  getAllLogs: async (success, limit = 100, offset = 0) => {
    const response = await apiClient.get('/admin/logs', {
      params: { success, limit, offset }
    });
    return response.data;
  },

  getAllPricing: async () => {
    const response = await apiClient.get('/admin/pricing');
    return response.data;
  },

  createVerificationType: async (name, price, description, input_label, input_fields) => {
    const response = await apiClient.post('/admin/pricing', {
      name,
      price,
      description,
      input_label,
      input_fields
    });
    return response.data;
  },

  updatePricing: async (typeId, updates) => {
    const response = await apiClient.put('/admin/pricing', {
      typeId,
      ...updates
    });
    return response.data;
  },

  deleteVerificationType: async (typeId) => {
    const response = await apiClient.delete(`/admin/pricing/${typeId}`);
    return response.data;
  }
};

/**
 * Payment API
 */
export const paymentAPI = {
  getCreditRate: async () => {
    const response = await apiClient.get('/payments/credit-rate');
    return response.data;
  },

  setCreditRate: async (rate) => {
    const response = await apiClient.post('/payments/credit-rate', { rate });
    return response.data;
  },

  getGSTRate: async () => {
    const response = await apiClient.get('/payments/gst-rate');
    return response.data;
  },

  setGSTRate: async (gst) => {
    const response = await apiClient.post('/payments/gst-rate', { gst });
    return response.data;
  },

  createOrder: async (credits) => {
    const response = await apiClient.post('/payments/create-order', { credits });
    return response.data;
  },

  verifyPayment: async (paymentData) => {
    const response = await apiClient.post('/payments/verify-payment', paymentData);
    return response.data;
  },

  getHistory: async () => {
    const response = await apiClient.get('/payments/history');
    return response.data;
  }
};

/**
 * Sub-User API
 */
export const subUserAPI = {
  sendOTP: async (email, name) => {
    const response = await apiClient.post('/subusers/send-otp', { email, name });
    return response.data;
  },

  verifyOTP: async (email, otp) => {
    const response = await apiClient.post('/subusers/verify-otp', { email, otp });
    return response.data;
  },

  create: async (data) => {
    const response = await apiClient.post('/subusers', data);
    return response.data;
  },

  getAll: async (limit = 100, offset = 0) => {
    const response = await apiClient.get('/subusers', {
      params: { limit, offset }
    });
    return response.data;
  },

  getDetails: async (subUserId) => {
    const response = await apiClient.get(`/subusers/${subUserId}`);
    return response.data;
  },

  update: async (subUserId, data) => {
    const response = await apiClient.put(`/subusers/${subUserId}`, data);
    return response.data;
  },

  delete: async (subUserId) => {
    const response = await apiClient.delete(`/subusers/${subUserId}`);
    return response.data;
  },

  toggleStatus: async (subUserId) => {
    const response = await apiClient.patch(`/subusers/${subUserId}/toggle-status`);
    return response.data;
  }
};

export default apiClient;
